/**
 * External Imports
 */
import { FC, useState, useMemo, useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';

/**
 * Nexus Imports
 */
import { NeuCheckbox } from '@neutron/react';

/**
 * Internal Imports
 */
import Table from '../../../shared/table';

import { setDischargeParams } from '../../../../redux/actions';
import { useGetPatientsToRoundByFacilityQuery } from '../../../../redux/apis/Users.api';

import { useDebounceValue } from '../../../../utils/debouncers';
import { calculateStayTime } from '../../../../utils/helpers';

/**
 * Global Type Definition Imports
 */
import { Patient } from '../../../../config/interfaces';
import {
  CellContainer,
  StyledLabel,
  StyledNeuTableRow
} from '../../../shared/table/Table.styles';

interface DischargeProps {
  searchTerm: string;
  facilityId: string;
}

const Discharge: FC<DischargeProps> = ({ searchTerm, facilityId }) => {
  const dispatch = useDispatch();

  const [sortKey, setSortKey] = useState<string>('firstName');
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('asc');
  const [active, setActive] = useState<string>('Patient');

  const accountsToDischarge = useRef<string[]>([]);

  // debouncing search term
  const debouncedNameSearchTerm = useDebounceValue(searchTerm, 275);

  const {
    data: patientsToRoundData = [],
    // isError: isGetPatientsToRoundByFacilityError,
    // isFetching: isGetPatientsToRoundByFacilityFetching,
    isLoading: isGetPatientsToRoundByFacilityLoading
  } = useGetPatientsToRoundByFacilityQuery(facilityId, {
    refetchOnMountOrArgChange: true,
    skip: !facilityId
  });

  const handleSort = (column: string) => {
    let key;
    switch (column) {
      case 'Current Patients':
        key = 'firstName';
        break;
      case 'Room':
        key = 'room';
        break;
      case 'Length Of Stay':
        key = 'lengthOfStay';
        break;
      default:
        key = '';
    }
    if (key === sortKey && sortDir === 'asc') {
      setSortDir('desc');
    } else {
      setSortDir('asc');
    }
    setActive(column);
    setSortKey(key);
  };

  const onCheck = (detail: any, accNum: string) => {
    const prevAccountsToDischarge = [...accountsToDischarge.current];
    if (detail.checked) {
      prevAccountsToDischarge.push(accNum);
    } else {
      const index = prevAccountsToDischarge.indexOf(accNum);
      prevAccountsToDischarge.splice(index, 1);
    }
    accountsToDischarge.current = prevAccountsToDischarge;

    const params = {
      facilityId,
      accountNumbers: accountsToDischarge.current
    };
    dispatch(setDischargeParams(params));
  };

  const filteredPatientsToDischarge = useMemo(() => {
    const q = new RegExp(
      debouncedNameSearchTerm.trim().replace(/\\/g, ''),
      'i'
    );

    return patientsToRoundData.filter(
      (patient: Patient) =>
        `${patient.firstName} ${patient.lastName}`.match(q) ||
        `${patient.lastName} ${patient.firstName}`.match(q)
    );
  }, [patientsToRoundData, debouncedNameSearchTerm]);

  const sortedPatientsToDischarge = useMemo(() => {
    const stateCopy = [...filteredPatientsToDischarge];
    const sorted = stateCopy.sort((a: Patient, b: Patient) => {
      if (sortKey === 'lengthOfStay') {
        if (a[sortKey] === b[sortKey]) {
          return 0;
        }
        if (!a[sortKey]) {
          return sortDir === 'asc' ? 1 : -1;
        }
        if (!b[sortKey]) {
          return sortDir === 'asc' ? -1 : 1;
        }
        if (sortDir === 'asc') {
          return a[sortKey] - b[sortKey];
        }
        return b[sortKey] - a[sortKey];
      }
      if (sortKey === 'firstName') {
        if (sortDir === 'asc') {
          return `${a.firstName} ${a.lastName}` > `${b.firstName} ${b.lastName}`
            ? 1
            : -1;
        }
        return `${a.firstName} ${a.lastName}` > `${b.firstName} ${b.lastName}`
          ? -1
          : 1;
      }
      if (a[sortKey] === b[sortKey]) {
        return 0;
      }
      if (!a[sortKey]) {
        return sortDir === 'asc' ? 1 : -1;
      }
      if (!b[sortKey]) {
        return sortDir === 'asc' ? -1 : 1;
      }
      if (sortDir === 'asc') {
        return a[sortKey]! > b[sortKey]! ? 1 : -1;
      }
      return a[sortKey]! > b[sortKey]! ? -1 : 1;
    });
    return sorted;
  }, [filteredPatientsToDischarge, sortDir, sortKey]);

  const isTableEmpty = useMemo(
    () => !debouncedNameSearchTerm && sortedPatientsToDischarge.length === 0,
    [debouncedNameSearchTerm, sortedPatientsToDischarge]
  );

  const isTableSearchEmpty = useMemo(
    () => debouncedNameSearchTerm && sortedPatientsToDischarge.length === 0,
    [debouncedNameSearchTerm, sortedPatientsToDischarge]
  );

  useEffect(() => {
    if (patientsToRoundData.length > 0) {
      accountsToDischarge.current = [];
    }
  }, [patientsToRoundData]);

  const headers: JSX.Element[] = [
    <CellContainer
      key="Current Patients"
      data-testid="discharge-table-patient-column"
      unit="column"
      colWidth={45}
      direction={!(active === 'Current Patients') ? 'none' : sortDir}
      sort
    >
      Current Patients
    </CellContainer>,
    <CellContainer
      key="Room"
      data-testid="discharge-table-room-column"
      unit="column"
      colWidth={20}
      direction={!(active === 'Room') ? 'none' : sortDir}
      sort
    >
      Room
    </CellContainer>,
    <CellContainer
      key="Length of Stay"
      data-testid="discharge-table-length-column"
      unit="column"
      colWidth={35}
      alignment="end"
      direction={!(active === 'Length Of Stay') ? 'none' : sortDir}
      sort
      styles="flex-direction: row;"
    >
      Length Of Stay
    </CellContainer>
  ];
  const rows = sortedPatientsToDischarge.map(
    (patient: Patient & { checked: boolean }) => (
      <StyledNeuTableRow
        key={`${patient.urn}-${patient.mrn}`}
        data-testid={`discharge-row-${patient.urn}-${patient.mrn}`}
        unit="row"
        size="default"
      >
        <CellContainer unit="column" colWidth={45}>
          <StyledLabel className="neu-text-blue-70">
            <NeuCheckbox
              color="blue"
              label={`${patient.firstName} ${patient.lastName}`}
              style={{ marginRight: 5 }}
              onNeuChange={e => onCheck(e.detail, patient.accountNum)}
            />
          </StyledLabel>
        </CellContainer>
        <CellContainer unit="column" colWidth={20}>
          <StyledLabel>{patient.room}</StyledLabel>
        </CellContainer>
        <CellContainer
          unit="column"
          colWidth={35}
          alignment="end"
          styles="flex-direction: row; align-items: center; padding-right: 3.5rem !important;"
        >
          <StyledLabel>
            {patient.lengthOfStay !== -1
              ? calculateStayTime(patient.lengthOfStay)
              : ''}
          </StyledLabel>
        </CellContainer>
      </StyledNeuTableRow>
    )
  );

  return (
    <Table
      customClass="cned-list-table"
      headers={headers}
      rows={rows}
      isTableEmpty={isTableEmpty}
      isTableEmptySearch={isTableSearchEmpty}
      loading={isGetPatientsToRoundByFacilityLoading}
      type="discharge"
      onSort={(e: any) => handleSort(e.detail.label)}
    />
  );
};

export default Discharge;
